import { useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles } from "lucide-react";

const ease = [0.22, 1, 0.36, 1];

// A short ascending shimmer of bells via WebAudio (fires on user-gesture-driven mount).
function playAscensionChime() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const now = ctx.currentTime;
    const master = ctx.createGain();
    master.gain.value = 0.0001;
    master.connect(ctx.destination);
    master.gain.setValueAtTime(0.0001, now);
    master.gain.exponentialRampToValueAtTime(0.5, now + 0.05);
    master.gain.exponentialRampToValueAtTime(0.0001, now + 4.2);

    const notes = [261.63, 329.63, 392.0, 523.25, 659.25, 783.99]; // C major ascent
    notes.forEach((f, i) => {
      const t = now + i * 0.28;
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "sine";
      o.frequency.value = f;
      const o2 = ctx.createOscillator();
      o2.type = "triangle";
      o2.frequency.value = f * 2.005;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.28, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 1.6);
      o.connect(g);
      o2.connect(g);
      g.connect(master);
      o.start(t);
      o2.start(t);
      o.stop(t + 1.7);
      o2.stop(t + 1.7);
    });
    setTimeout(() => ctx.close().catch(() => {}), 5000);
  } catch (e) {}
}

export const AscensionRitual = ({ tier, onClose }) => {
  const timer = useRef(null);
  useEffect(() => {
    if (!tier) return;
    playAscensionChime();
    timer.current = setTimeout(() => onClose && onClose(), 5200);
    return () => timer.current && clearTimeout(timer.current);
  }, [tier]);

  return (
    <AnimatePresence>
      {tier && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.8 }}
          onClick={onClose}
          className="fixed inset-0 z-[100] flex items-center justify-center overflow-hidden bg-black/92 backdrop-blur-xl"
          data-testid="ascension-ritual"
        >
          {/* unfurling light */}
          <motion.div
            initial={{ scale: 0, opacity: 0.9 }}
            animate={{ scale: 6, opacity: 0 }}
            transition={{ duration: 2.6, ease }}
            className="absolute h-[42vh] w-[42vh] rounded-full"
            style={{ background: "radial-gradient(circle, rgba(197,160,89,0.55) 0%, rgba(197,160,89,0) 70%)" }}
          />
          <motion.div
            initial={{ scale: 0, opacity: 0.7 }}
            animate={{ scale: 9, opacity: 0 }}
            transition={{ duration: 3.2, ease, delay: 0.5 }}
            className="absolute h-[42vh] w-[42vh] rounded-full"
            style={{ background: "radial-gradient(circle, rgba(120,90,180,0.4) 0%, rgba(120,90,180,0) 70%)" }}
          />
          {/* radiant rays */}
          {Array.from({ length: 14 }).map((_, i) => (
            <motion.span
              key={i}
              initial={{ scaleY: 0, opacity: 0 }}
              animate={{ scaleY: 1, opacity: [0, 0.6, 0] }}
              transition={{ duration: 2.4, delay: 0.3 + i * 0.04, ease }}
              className="absolute h-[60vh] w-[2px] origin-center"
              style={{
                background: "linear-gradient(to top, rgba(197,160,89,0), rgba(197,160,89,0.7), rgba(197,160,89,0))",
                transform: `rotate(${(360 / 14) * i}deg)`,
              }}
            />
          ))}
          {/* rising sparks */}
          {Array.from({ length: 28 }).map((_, i) => (
            <motion.span
              key={`p${i}`}
              initial={{ y: 120, x: 0, opacity: 0 }}
              animate={{ y: -260 - Math.random() * 160, opacity: [0, 1, 0] }}
              transition={{ duration: 2.6 + Math.random() * 1.4, delay: 0.4 + Math.random(), ease }}
              className="absolute h-1 w-1 rounded-full bg-[#C5A059]"
              style={{ left: `${42 + Math.random() * 16}%` }}
            />
          ))}

          <div className="relative z-10 px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.6, ease }}
              className="mb-6 flex justify-center"
            >
              <Sparkles size={34} className="text-[#C5A059] [filter:drop-shadow(0_0_18px_rgba(197,160,89,0.8))]" />
            </motion.div>
            <motion.p
              initial={{ opacity: 0, letterSpacing: "0.1em" }}
              animate={{ opacity: 1, letterSpacing: "0.4em" }}
              transition={{ duration: 1.2, delay: 0.7, ease }}
              className="mb-5 text-[0.7rem] uppercase text-white/50"
            >
              You ascend
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, scale: 0.9, filter: "blur(10px)" }}
              animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
              transition={{ duration: 1.3, delay: 0.9, ease }}
              className="font-serif text-[clamp(2.4rem,7vw,5rem)] font-light italic text-[#C5A059] [text-shadow:0_0_40px_rgba(197,160,89,0.5)]"
              data-testid="ascension-ritual-tier"
            >
              {tier}
            </motion.h2>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1.2, delay: 1.6 }}
              className="mt-6 font-serif text-lg font-light italic text-white/60"
            >
              The chambers unfurl before you.
            </motion.p>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 2.6 }}
              className="mt-10 text-[0.6rem] uppercase tracking-[0.3em] text-white/25"
            >
              Touch to continue
            </motion.p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AscensionRitual;
